import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class GraphQLOptimizerService extends Service {
  @service('network-retry') networkRetry;

  @tracked queryCache = new Map();
  @tracked queryBatches = new Map();

  // Cache TTL in milliseconds
  CACHE_TTL = {
    short: 5 * 60 * 1000, // 5 minutes for frequently changing data
    medium: 30 * 60 * 1000, // 30 minutes for semi-static data
    long: 2 * 60 * 60 * 1000, // 2 hours for relatively static data
  };

  /**
   * Optimizes a GraphQL query by adding performance directives and caching
   * @param {string} query - The GraphQL query string
   * @param {Object} variables - Query variables
   * @param {Object} options - Optimization options
   * @returns {Object} Optimized query configuration
   */
  optimizeQuery(query, variables = {}, options = {}) {
    const {
      cacheTtl = this.CACHE_TTL.medium,
      enableBatching = false,
      priority = 'normal',
      includeFragments = true,
    } = options;

    // Generate cache key
    const cacheKey = this.generateCacheKey(query, variables);

    // Check cache first
    if (this.isQueryCached(cacheKey, cacheTtl)) {
      return {
        fromCache: true,
        data: this.getCachedQuery(cacheKey).data,
      };
    }

    // Add performance directives to query
    const optimizedQuery = this.addPerformanceDirectives(query, options);

    // Add fragments for common fields if requested
    const queryWithFragments = includeFragments
      ? this.addCommonFragments(optimizedQuery)
      : optimizedQuery;

    return {
      query: queryWithFragments,
      variables,
      cacheKey,
      cacheTtl,
      enableBatching,
      priority,
    };
  }

  /**
   * Executes an optimized GraphQL query with caching and retry logic
   * @param {Function} queryFn - Function to execute the query
   * @param {Object} queryConfig - Optimized query configuration
   * @param {Object} context - Route or component context
   * @returns {Promise<any>} Query result
   */
  async executeOptimizedQuery(queryFn, queryConfig, context) {
    // Return cached result if available
    if (queryConfig.fromCache) {
      return queryConfig.data;
    }

    try {
      // Execute query with retry logic
      const result = await this.networkRetry.withRetry(queryFn, {
        maxRetries: 2,
        baseDelay: 500,
        onRetry: (error, attempt) => {
          console.warn(`GraphQL query retry ${attempt}:`, error.message);
        },
      });

      // Cache the result
      this.cacheQueryResult(queryConfig.cacheKey, result, queryConfig.cacheTtl);

      return result;
    } catch (error) {
      console.error('Optimized GraphQL query failed:', error);
      throw error;
    }
  }

  /**
   * Adds performance directives to GraphQL queries
   * @param {string} query - Original query
   * @param {Object} options - Performance options
   * @returns {string} Query with performance directives
   */
  addPerformanceDirectives(query, options) {
    let optimizedQuery = query;

    // Add @cached directive for appropriate fields
    if (options.enableCaching !== false) {
      optimizedQuery = this.addCacheDirectives(optimizedQuery);
    }

    // Add @defer for non-critical fields
    if (options.enableDefer) {
      optimizedQuery = this.addDeferDirectives(optimizedQuery);
    }

    // Add field-level directives for image optimization
    optimizedQuery = this.addImageOptimizationDirectives(optimizedQuery);

    return optimizedQuery;
  }

  /**
   * Adds common GraphQL fragments to reduce query duplication
   * @param {string} query - Original query
   * @returns {string} Query with common fragments
   */
  addCommonFragments(query) {
    const authorFragment = `
      fragment AuthorInfo on Author {
        displayName
        profilePicture {
          url(transform: { width: 48, height: 48, resizeStrategy: THUMB })
        }
      }
    `;

    const coverImageFragment = `
      fragment CoverImageInfo on Asset {
        url(transform: { width: 400, height: 240, resizeStrategy: FILL })
      }
    `;

    const basicArticleFragment = `
      fragment BasicArticleInfo on Article {
        title
        slug
        shortDescription
        publishedDate
        coverImage {
          ...CoverImageInfo
        }
        author {
          ...AuthorInfo
        }
      }
    `;

    // Add fragments to query if they're used
    let queryWithFragments = query;

    if (query.includes('author {') && !query.includes('fragment AuthorInfo')) {
      queryWithFragments = authorFragment + '\n\n' + queryWithFragments;
    }

    if (
      query.includes('coverImage {') &&
      !query.includes('fragment CoverImageInfo')
    ) {
      queryWithFragments = coverImageFragment + '\n\n' + queryWithFragments;
    }

    if (
      query.includes('title\n') &&
      !query.includes('fragment BasicArticleInfo')
    ) {
      queryWithFragments = basicArticleFragment + '\n\n' + queryWithFragments;
    }

    return queryWithFragments;
  }

  /**
   * Adds cache directives to appropriate fields
   * @param {string} query - Original query
   * @returns {string} Query with cache directives
   */
  addCacheDirectives(query) {
    // Add cache directives to relatively static fields
    return query
      .replace(/author \{/g, 'author @cached(ttl: 3600) {')
      .replace(/profilePicture \{/g, 'profilePicture @cached(ttl: 7200) {');
  }

  /**
   * Adds defer directives for non-critical fields
   * @param {string} query - Original query
   * @returns {string} Query with defer directives
   */
  addDeferDirectives(query) {
    // Defer body content for list queries (it's not immediately visible)
    return query.replace(/body \{([^}]+)\}/g, 'body @defer { $1 }');
  }

  /**
   * Adds image optimization directives
   * @param {string} query - Original query
   * @returns {string} Query with image optimization
   */
  addImageOptimizationDirectives(query) {
    // Add responsive image transforms
    return query
      .replace(
        /url(?!\()/g,
        'url(transform: { width: 400, height: 240, resizeStrategy: FILL, format: WEBP })',
      )
      .replace(
        /profilePicture \{\s*url/g,
        'profilePicture {\n        url(transform: { width: 48, height: 48, resizeStrategy: THUMB, format: WEBP })',
      );
  }

  /**
   * Optimizes list queries by removing unnecessary fields and adding pagination
   * @param {string} originalQuery - The original query
   * @param {Object} options - Pagination and field options
   * @returns {string} Optimized list query
   */
  optimizeListQuery(originalQuery, options = {}) {
    const {
      limit = 10,
      skip = 0,
      excludeBody = true,
      optimizeImages = true,
      fields = null,
    } = options;

    let optimizedQuery = originalQuery;

    // Remove body content for list views if requested
    if (excludeBody) {
      optimizedQuery = optimizedQuery.replace(/body \{[^}]*\}/g, '');
      optimizedQuery = optimizedQuery.replace(/\bbody\b(?!\s*\()/g, '');
    }

    // Add pagination parameters
    optimizedQuery = optimizedQuery.replace(
      /Collection\(/g,
      `Collection(limit: ${limit}, skip: ${skip}, `,
    );

    // Optimize image sizes for list views
    if (optimizeImages) {
      optimizedQuery = optimizedQuery.replace(
        /coverImage \{\s*url/g,
        'coverImage {\n        url(transform: { width: 300, height: 180, resizeStrategy: FILL, format: WEBP })',
      );
    }

    // Include only specified fields if provided
    if (fields && Array.isArray(fields)) {
      // This is complex to implement generically, would need query parsing
      console.warn('Field filtering not yet implemented');
    }

    return optimizedQuery;
  }

  /**
   * Generates a cache key for a query and variables
   * @param {string} query - GraphQL query
   * @param {Object} variables - Query variables
   * @returns {string} Cache key
   */
  generateCacheKey(query, variables) {
    const normalizedQuery = query.replace(/\s+/g, ' ').trim();
    const variablesString = JSON.stringify(variables || {});
    return `gql:${btoa(normalizedQuery + variablesString)}`;
  }

  /**
   * Checks if a query is cached and still valid
   * @param {string} cacheKey - Cache key
   * @param {number} ttl - Time to live in milliseconds
   * @returns {boolean} Whether query is cached and valid
   */
  isQueryCached(cacheKey, ttl) {
    const cached = this.queryCache.get(cacheKey);
    if (!cached) return false;

    const now = Date.now();
    return now - cached.timestamp < ttl;
  }

  /**
   * Gets cached query result
   * @param {string} cacheKey - Cache key
   * @returns {Object} Cached query result
   */
  getCachedQuery(cacheKey) {
    return this.queryCache.get(cacheKey);
  }

  /**
   * Caches a query result
   * @param {string} cacheKey - Cache key
   * @param {any} data - Query result data
   * @param {number} ttl - Time to live in milliseconds
   */
  cacheQueryResult(cacheKey, data, ttl) {
    this.queryCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });

    // Clean up expired cache entries periodically
    this.scheduleCleanup();
  }

  /**
   * Clears expired cache entries
   */
  cleanupExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.queryCache.entries()) {
      if (now - value.timestamp > value.ttl) {
        this.queryCache.delete(key);
      }
    }
  }

  /**
   * Schedules cache cleanup
   */
  scheduleCleanup() {
    if (!this._cleanupTimer) {
      this._cleanupTimer = setTimeout(
        () => {
          this.cleanupExpiredCache();
          this._cleanupTimer = null;
        },
        5 * 60 * 1000,
      ); // Cleanup every 5 minutes
    }
  }

  /**
   * Clears all cached queries
   */
  clearCache() {
    this.queryCache.clear();
  }

  /**
   * Gets cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    let totalSize = 0;
    let expiredCount = 0;
    const now = Date.now();

    for (const [key, value] of this.queryCache.entries()) {
      totalSize += JSON.stringify(value.data).length;
      if (now - value.timestamp > value.ttl) {
        expiredCount++;
      }
    }

    return {
      totalEntries: this.queryCache.size,
      expiredEntries: expiredCount,
      approximateSize: totalSize,
      hitRate: this._hitRate || 0,
    };
  }
}
